/**
 * Manual Override Tab — Complete Redesign
 *
 * Monthly column grid matching the SKU Projection layout.
 * Features:
 *  - Same sticky columns as ProjectionGridTab (Store, SKU, Product, Brand, Category, Family, Status, ABC, Avg Sales 6M)
 *  - Monthly columns with inline editing: click cell → TextField, blur/Enter → save to local state
 *  - Change % badge auto-calculated per cell
 *  - Bulk modification panel: date range, change %, reason, Apply/Save/Reset
 *  - Fullscreen mode with ESC handler
 *  - Server-side pagination & sorting
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Autocomplete, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, Chip, TablePagination, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Snackbar,
} from '@mui/material';
import {
  Edit, Save, Refresh, Fullscreen, FullscreenExit, RestartAlt,
  ArrowUpward, ArrowDownward, UnfoldLess, UnfoldMore,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useForecastingFilters } from './ForecastingFilterContext';
import ExportMenu from '../Common/ExportMenu';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STICKY_COLS = [
  { key: 'store_name', label: 'Store', width: 110, sortable: true, collapsible: false },
  { key: 'brand', label: 'Brand', width: 72, sortable: true, collapsible: true },
  { key: 'sku', label: 'SKU', width: 90, sortable: true, collapsible: false },
  { key: 'product_name', label: 'Product', width: 120, sortable: true, collapsible: false },
  { key: 'category', label: 'Cat.', width: 62, sortable: true, collapsible: true },
  { key: 'product_family', label: 'Family', width: 80, sortable: true, collapsible: true },
  { key: 'lifecycle_stage', label: 'Item Status', width: 85, sortable: true, collapsible: true },
  { key: 'abc_classification', label: 'ABC', width: 40, sortable: true, collapsible: true },
  { key: 'average_sales_6m', label: 'Avg 6M', width: 58, sortable: true, collapsible: true },
];

const getVisibleCols = (expanded: boolean) => {
  const cols = expanded ? STICKY_COLS : STICKY_COLS.filter((c) => !c.collapsible);
  let left = 0;
  return cols.map((col) => {
    const entry = { ...col, left };
    left += col.width;
    return entry;
  });
};

const statusColor: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  NEW: 'info', GROWTH: 'success', MATURE: 'default',
  DECLINING: 'warning', PHASE_OUT: 'error', DISCONTINUED: 'error',
};

/* Override map key: product_id__store_id__YYYY-MM */
type OverrideKey = string;
interface OverrideEntry {
  original: number;
  override: number;
  reason?: string;
}

const mkKey = (productId: string, storeId: string, month: string): OverrideKey =>
  `${productId}__${storeId}__${month}`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const ManualOverrideTab: React.FC = () => {
  const { brands, categories, stores: selectedStoreIds, cities } = useForecastingFilters();
  const queryClient = useQueryClient();

  /* Grid state */
  const [months, setMonths] = useState(18);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState('store_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [shouldLoad, setShouldLoad] = useState(false);
  const [colsExpanded, setColsExpanded] = useState(true);
  const visibleCols = useMemo(() => getVisibleCols(colsExpanded), [colsExpanded]);

  /* Override local state */
  const [localOverrides, setLocalOverrides] = useState<Map<OverrideKey, OverrideEntry>>(new Map());
  const [editingCell, setEditingCell] = useState<OverrideKey | null>(null);
  const editRef = useRef<HTMLInputElement>(null);

  /* Local fine-grained filters */
  const [familyFilter, setFamilyFilter] = useState('');
  const [skuFilter, setSkuFilter] = useState('');
  const [familyInput, setFamilyInput] = useState('');
  const [skuInput, setSkuInput] = useState('');
  const familyDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skuDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [familySearch, setFamilySearch] = useState('');
  const [skuSearch, setSkuSearch] = useState('');

  /* Autocomplete options */
  const { data: familyOptions = [] } = useQuery<string[]>({
    queryKey: ['autocomplete-family', familySearch, brands, categories],
    queryFn: () => api.getProductAutocomplete(
      'product_family',
      familySearch || undefined,
      brands.length > 0 ? brands.join(',') : undefined,
      categories.length > 0 ? categories.join(',') : undefined,
    ),
    staleTime: 30_000,
  });

  const { data: skuOptions = [] } = useQuery<string[]>({
    queryKey: ['autocomplete-sku', skuSearch, brands, categories],
    queryFn: () => api.getProductAutocomplete(
      'sku',
      skuSearch || undefined,
      brands.length > 0 ? brands.join(',') : undefined,
      categories.length > 0 ? categories.join(',') : undefined,
    ),
    staleTime: 30_000,
  });

  /* Bulk modification panel */
  const [bulkStartDate, setBulkStartDate] = useState<Date | null>(null);
  const [bulkEndDate, setBulkEndDate] = useState<Date | null>(null);
  const [bulkChangePct, setBulkChangePct] = useState<string>('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkAbcFilter, setBulkAbcFilter] = useState<string>('');

  /* Fullscreen */
  const [isFullscreen, setIsFullscreen] = useState(false);

  /* Notifications */
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  /* Reset confirmation dialog */
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  /* Saving state */
  const [isSaving, setIsSaving] = useState(false);

  /* ---- Data queries ---- */

  const { data: projectionData, isLoading, error, refetch } = useQuery({
    queryKey: ['override-projection', brands, categories, selectedStoreIds, cities, familyFilter, skuFilter, bulkAbcFilter, months, page, rowsPerPage, sortBy, sortDir],
    queryFn: async () => {
      const response = await api.getForecastProjection(
        undefined,
        selectedStoreIds.length === 1 ? selectedStoreIds[0] : undefined,
        months,
        {
          brand: brands.length > 0 ? brands.join(',') : undefined,
          category: categories.length > 0 ? categories.join(',') : undefined,
          city: cities.length > 0 ? cities.join(',') : undefined,
          product_family: familyFilter || undefined,
          sku: skuFilter || undefined,
          abc_classification: bulkAbcFilter || undefined,
          page: page + 1,
          page_size: rowsPerPage,
          sort_by: sortBy,
          sort_dir: sortDir,
        },
      );
      return response.data;
    },
    enabled: shouldLoad,
  });

  /* Load existing overrides from backend */
  const { data: serverOverrides } = useQuery({
    queryKey: ['projection-overrides', brands, categories, selectedStoreIds, cities, familyFilter, skuFilter, months],
    queryFn: async () => {
      const response = await api.getProjectionOverrides({
        brand: brands.length > 0 ? brands.join(',') : undefined,
        category: categories.length > 0 ? categories.join(',') : undefined,
        city: cities.length > 0 ? cities.join(',') : undefined,
        store_id: selectedStoreIds.length === 1 ? selectedStoreIds[0] : undefined,
        product_family: familyFilter || undefined,
        sku: skuFilter || undefined,
        months,
      });
      return response.data;
    },
    enabled: shouldLoad,
  });

  /* Merge server overrides into local state on load */
  useEffect(() => {
    if (!serverOverrides?.overrides) return;
    setLocalOverrides((prev) => {
      const next = new Map(prev);
      const detailMap = new Map<string, any>();
      (serverOverrides.override_details || []).forEach((d: any) => {
        detailMap.set(`${d.product_id}__${d.store_id}__${d.month_key}`, d);
      });
      Object.entries(serverOverrides.overrides as Record<string, number>).forEach(([key, val]) => {
        if (!next.has(key)) {
          const detail = detailMap.get(key);
          next.set(key, {
            original: detail?.original_forecast ?? 0,
            override: val,
            reason: detail?.override_reason || '',
          });
        }
      });
      return next;
    });
  }, [serverOverrides]);

  /* ---- Handlers ---- */

  const handleLoadProjection = () => {
    setShouldLoad(true);
    refetch();
  };

  const handleSort = useCallback((column: string) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  }, [sortBy]);

  /* Inline cell editing */
  const handleCellClick = (key: OverrideKey, original: number) => {
    if (editingCell === key) return;
    setEditingCell(key);
    if (!localOverrides.has(key)) {
      setLocalOverrides((prev) => {
        const next = new Map(prev);
        next.set(key, { original, override: original, reason: '' });
        return next;
      });
    }
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      setEditingCell(null);
    }
    if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const handleOverrideValueChange = (key: OverrideKey, value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return;
    setLocalOverrides((prev) => {
      const next = new Map(prev);
      const entry = next.get(key);
      if (entry) {
        next.set(key, { ...entry, override: num });
      }
      return next;
    });
  };

  /* Bulk apply */
  const handleBulkApply = () => {
    if (!projectionData?.rows || !bulkChangePct) return;
    const pct = parseFloat(bulkChangePct);
    if (isNaN(pct)) return;

    const monthHeaders: string[] = projectionData.month_headers || [];

    /* Normalise DatePicker values to 1st-of-month so the day component
       (carried over from "today") cannot cause off-by-one issues. */
    const rangeStart = bulkStartDate
      ? new Date(bulkStartDate.getFullYear(), bulkStartDate.getMonth(), 1)
      : null;
    const rangeEnd = bulkEndDate
      ? new Date(bulkEndDate.getFullYear(), bulkEndDate.getMonth(), 1)
      : null;

    setLocalOverrides((prev) => {
      const next = new Map(prev);

      projectionData.rows.forEach((row: any) => {
        // Skip rows that don't match ABC filter
        if (bulkAbcFilter && (row.abc_classification || '').toUpperCase() !== bulkAbcFilter.toUpperCase()) return;

        monthHeaders.forEach((header: string) => {
          // Check if this month falls in the bulk date range
          if (rangeStart || rangeEnd) {
            // Headers are "May 2026" format (%b %Y) — parse accordingly
            const headerDate = new Date(`1 ${header}`);
            const monthDate = new Date(headerDate.getFullYear(), headerDate.getMonth(), 1);
            if (rangeStart && monthDate < rangeStart) return;
            if (rangeEnd && monthDate > rangeEnd) return;
          }

          const original = row.monthly_forecast?.[header] || 0;
          if (original === 0) return;

          const key = mkKey(row.product_id, row.store_id, header);
          const existing = next.get(key);
          const baseVal = existing ? existing.original : original;
          const newVal = Math.round(baseVal * (1 + pct / 100));

          next.set(key, {
            original: baseVal,
            override: newVal,
            reason: bulkReason || `Bulk ${pct > 0 ? '+' : ''}${pct}%`,
          });
        });
      });

      return next;
    });

    setSnackbar({ open: true, message: `Bulk override applied: ${pct > 0 ? '+' : ''}${pct}% across selected range`, severity: 'success' });
  };

  /* Save overrides to backend */
  const handleSaveOverrides = async () => {
    if (localOverrides.size === 0) return;
    setIsSaving(true);

    try {
      // Only save entries where override !== original
      const changed = Array.from(localOverrides.entries())
        .filter(([, entry]) => entry.override !== entry.original)
        .map(([key, entry]) => {
          const [productId, storeId, monthKey] = key.split('__');
          return {
            product_id: productId,
            store_id: storeId,
            forecast_date: `${monthKey}-01`,
            override_forecast: entry.override,
            override_reason: entry.reason || 'Manual adjustment',
            override_category: 'manual',
          };
        });

      if (changed.length === 0) {
        setSnackbar({ open: true, message: 'No changes to save', severity: 'success' });
        setIsSaving(false);
        return;
      }

      await api.bulkCreateOverrides(changed);
      setSnackbar({ open: true, message: `Saved ${changed.length} override(s) successfully`, severity: 'success' });

      // Mark all saved entries as clean so the "unsaved changes" chip
      // disappears and the Save button disables itself until the user makes
      // a new edit. We rebase `original` onto the just-saved `override`
      // (rather than clearing the map) so the UI keeps showing the override
      // values while the server query re-fetches.
      setLocalOverrides((prev) => {
        const next = new Map(prev);
        next.forEach((entry, key) => {
          if (entry.override !== entry.original) {
            next.set(key, { ...entry, original: entry.override });
          }
        });
        return next;
      });

      // Refresh server overrides
      queryClient.invalidateQueries({ queryKey: ['projection-overrides'] });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Failed to save: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  /* Reset to initial forecast */
  const handleResetOverrides = async () => {
    setResetDialogOpen(false);
    setIsSaving(true);

    try {
      await api.bulkDeleteOverrides({
        brand: brands.length > 0 ? brands.join(',') : undefined,
        category: categories.length > 0 ? categories.join(',') : undefined,
        city: cities.length > 0 ? cities.join(',') : undefined,
        store_id: selectedStoreIds.length === 1 ? selectedStoreIds[0] : undefined,
      });
      setLocalOverrides(new Map());
      queryClient.invalidateQueries({ queryKey: ['projection-overrides'] });
      setSnackbar({ open: true, message: 'All overrides reset to initial forecast', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Reset failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  /* Rows for <ExportMenu> */
  const exportRows = useMemo(() => {
    if (!projectionData?.rows) return [];
    const allHeaders: string[] = projectionData.month_headers || [];
    return projectionData.rows.map((row: any) => {
      const obj: Record<string, any> = {
        Store: row.store_name,
        SKU: row.sku,
        Product: row.product_name,
        Brand: row.brand || '',
        Category: row.category || '',
        Family: row.product_family || '',
        Status: row.lifecycle_stage || '',
        ABC: row.abc_classification || '',
        'Avg Sales (6M)': Math.round(row.average_sales_6m ?? 0),
      };
      allHeaders.forEach((h: string) => {
        const original = row.monthly_forecast?.[h] || 0;
        const key = mkKey(row.product_id, row.store_id, h);
        const entry = localOverrides.get(key);
        obj[`${h} Original`] = original;
        obj[`${h} Override`] = entry ? entry.override : original;
        if (entry && entry.override !== entry.original) {
          const diff = entry.override - entry.original;
          const pct = entry.original > 0 ? ((diff / entry.original) * 100).toFixed(1) : '0';
          obj[`${h} Change %`] = `${pct}%`;
        } else {
          obj[`${h} Change %`] = '';
        }
      });
      return obj;
    });
  }, [projectionData, localOverrides]);

  /* Fullscreen ESC handler */
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  /* ---- Derived data ---- */

  const monthHeaders: string[] = projectionData?.month_headers || [];
  const dataRows: any[] = projectionData?.rows || [];
  const totalRows = projectionData?.total_rows || 0;

  const dirtyCount = useMemo(() => {
    let count = 0;
    localOverrides.forEach((entry) => {
      if (entry.override !== entry.original) count++;
    });
    return count;
  }, [localOverrides]);

  const cellValue = (row: any, key: string) => {
    switch (key) {
      case 'average_sales_6m':
        // Average product quantity — show as a rounded integer, no decimals.
        return row.average_sales_6m != null
          ? Math.round(row.average_sales_6m).toLocaleString()
          : '—';
      default: return row[key] || '—';
    }
  };

  /* ---- Render ---- */

  const tableContent = (
    <>
      {/* Header bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
          Manual Forecast Override
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {dirtyCount > 0 && (
            <Chip
              label={`${dirtyCount} unsaved change${dirtyCount > 1 ? 's' : ''}`}
              color="warning"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
          {projectionData && dataRows.length > 0 && (
            <>
              <ExportMenu
                data={exportRows}
                filename={`IRIS_Override_Export_${new Date().toISOString().slice(0, 10)}`}
                title="Manual Forecast Override"
                sheetName="Manual Overrides"
              />
              <Tooltip title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}>
                <IconButton size="small" onClick={() => setIsFullscreen(!isFullscreen)}>
                  {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click any monthly cell to override its forecast value. Use bulk modification to apply a percentage change across a date range.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as any)?.response?.data?.detail || 'Failed to load projection data'}
        </Alert>
      )}

      {/* Controls row */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth size="small" type="number" label="Months"
            value={months}
            onChange={(e) => setMonths(Math.max(6, Math.min(24, parseInt(e.target.value) || 18)))}
            inputProps={{ min: 6, max: 24 }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            onClick={handleLoadProjection}
            disabled={isLoading}
            fullWidth sx={{ height: '40px' }}
          >
            {isLoading ? 'Loading...' : 'Load Grid'}
          </Button>
        </Grid>
      </Grid>

      {/* Bulk modification panel — only when data is loaded */}
      {projectionData && dataRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'var(--color-bg-secondary)' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'var(--color-text-primary)' }}>
            Bulk Modification
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6} sm={3} md>
                <DatePicker
                  label="Start Month"
                  views={['year', 'month']}
                  value={bulkStartDate}
                  onChange={(d) => setBulkStartDate(d)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md>
                <DatePicker
                  label="End Month"
                  views={['year', 'month']}
                  value={bulkEndDate}
                  onChange={(d) => setBulkEndDate(d)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6} sm={4} md>
                <Autocomplete
                  freeSolo size="small"
                  options={familyOptions}
                  value={familyFilter || null}
                  onChange={(_, v) => { setFamilyFilter(typeof v === 'string' ? v : v || ''); setPage(0); }}
                  inputValue={familyInput}
                  onInputChange={(_, v, reason) => {
                    setFamilyInput(v);
                    if (reason === 'input') {
                      if (familyDebounce.current) clearTimeout(familyDebounce.current);
                      familyDebounce.current = setTimeout(() => setFamilySearch(v), 250);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Family" placeholder="Sauvage..." />}
                  filterOptions={(x) => x}
                  clearOnBlur={false}
                />
              </Grid>
              <Grid item xs={6} sm={4} md>
                <Autocomplete
                  freeSolo size="small"
                  options={skuOptions}
                  value={skuFilter || null}
                  onChange={(_, v) => { setSkuFilter(typeof v === 'string' ? v : v || ''); setPage(0); }}
                  inputValue={skuInput}
                  onInputChange={(_, v, reason) => {
                    setSkuInput(v);
                    if (reason === 'input') {
                      if (skuDebounce.current) clearTimeout(skuDebounce.current);
                      skuDebounce.current = setTimeout(() => setSkuSearch(v), 250);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="SKU" placeholder="DIO-FRA..." />}
                  filterOptions={(x) => x}
                  clearOnBlur={false}
                />
              </Grid>
              <Grid item xs={4} sm={2} md={1}>
                <TextField
                  select fullWidth size="small" label="ABC"
                  value={bulkAbcFilter}
                  onChange={(e) => setBulkAbcFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                  <MenuItem value="B">B</MenuItem>
                  <MenuItem value="C">C</MenuItem>
                  <MenuItem value="D">D</MenuItem>
                  <MenuItem value="E">E</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4} sm={2} md={1}>
                <TextField
                  fullWidth size="small" type="number" label="Change %"
                  placeholder="±10"
                  value={bulkChangePct}
                  onChange={(e) => setBulkChangePct(e.target.value)}
                  inputProps={{ step: 1 }}
                />
              </Grid>
              <Grid item xs={8} sm={4} md={2}>
                <TextField
                  fullWidth size="small" label="Reason"
                  placeholder="Promo, seasonal..."
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained" size="small"
                    startIcon={<Edit sx={{ fontSize: 16 }} />}
                    onClick={handleBulkApply}
                    disabled={!bulkChangePct}
                  >
                    Apply Override
                  </Button>
                  <Button
                    variant="contained" color="success" size="small"
                    startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
                    onClick={handleSaveOverrides}
                    disabled={dirtyCount === 0 || isSaving}
                  >
                    Save ({dirtyCount})
                  </Button>
                  <Button
                    variant="outlined" color="error" size="small"
                    startIcon={<RestartAlt sx={{ fontSize: 16 }} />}
                    onClick={() => setResetDialogOpen(true)}
                    disabled={localOverrides.size === 0}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Paper>
      )}

      {/* Data grid */}
      {projectionData && dataRows.length > 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {totalRows} row{totalRows > 1 ? 's' : ''} — {months} months
              </Typography>
              <Tooltip title={colsExpanded ? 'Collapse detail columns' : 'Expand detail columns'}>
                <IconButton size="small" onClick={() => setColsExpanded((v) => !v)} sx={{ color: 'var(--color-text-muted)' }}>
                  {colsExpanded ? <UnfoldLess sx={{ fontSize: 18 }} /> : <UnfoldMore sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            </Box>
            <TablePagination
              component="div" count={totalRows} page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '12px' } }}
            />
          </Box>

          <TableContainer
            component={Paper}
            sx={{ maxHeight: isFullscreen ? 'calc(100vh - 200px)' : 'calc(100vh - 420px)', overflowX: 'auto' }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {visibleCols.map((col, i) => (
                    <TableCell
                      key={col.key}
                      onClick={() => col.sortable && handleSort(col.key)}
                      sx={{
                        fontWeight: 700, fontSize: '10px', position: 'sticky', left: col.left,
                        bgcolor: 'var(--color-surface)', zIndex: 3, minWidth: col.width, maxWidth: col.width,
                        borderRight: i === visibleCols.length - 1
                          ? '2px solid var(--color-border)' : '1px solid var(--color-border)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', px: '6px',
                        cursor: col.sortable ? 'pointer' : 'default', userSelect: 'none',
                        '&:hover': col.sortable ? { bgcolor: 'var(--color-bg-secondary)' } : {},
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {col.label}
                        {sortBy === col.key && (
                          sortDir === 'asc'
                            ? <ArrowUpward sx={{ fontSize: 12, color: 'var(--color-accent)' }} />
                            : <ArrowDownward sx={{ fontSize: 12, color: 'var(--color-accent)' }} />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                  {monthHeaders.map((header) => {
                    const parts = header.split(' ');
                    const monthName = parts[0] || header;
                    const year = parts[1] || '';
                    return (
                      <TableCell
                        key={header} align="right"
                        sx={{ fontWeight: 700, fontSize: '10px', minWidth: 48, maxWidth: 56, px: '4px', lineHeight: 1.2 }}
                      >
                        {monthName}<br /><span style={{ fontWeight: 500, fontSize: '9px', color: 'var(--color-text-muted)' }}>{year}</span>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: '10px', minWidth: 52, borderLeft: '2px solid var(--color-border)', px: '4px' }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataRows.map((row: any, idx: number) => {
                  const rowBg = idx % 2 === 0 ? 'var(--color-surface)' : 'var(--color-bg-secondary)';
                  let rowTotal = 0;

                  return (
                    <TableRow key={`${row.store_id}-${row.product_id}`} hover>
                      {/* Sticky columns */}
                      {visibleCols.map((col, i) => (
                        <TableCell
                          key={col.key}
                          sx={{
                            fontSize: '11px', position: 'sticky', left: col.left,
                            bgcolor: rowBg, zIndex: 1,
                            borderRight: i === visibleCols.length - 1
                              ? '2px solid var(--color-border)' : '1px solid var(--color-border)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            minWidth: col.width, maxWidth: col.width, px: '6px',
                            ...(col.key === 'sku' ? { fontFamily: 'monospace', fontSize: '10px' } : {}),
                          }}
                        >
                          {col.key === 'lifecycle_stage' && row.lifecycle_stage ? (
                            <Chip
                              label={row.lifecycle_stage} size="small"
                              color={statusColor[row.lifecycle_stage] || 'default'}
                              variant="outlined" sx={{ fontSize: '10px', height: 20 }}
                            />
                          ) : col.key === 'abc_classification' && row.abc_classification ? (
                            <Chip
                              label={row.abc_classification} size="small" variant="filled"
                              sx={{
                                fontSize: '11px', height: 20, fontWeight: 700,
                                bgcolor: row.abc_classification === 'A' ? '#e8f5e9'
                                  : row.abc_classification === 'B' ? '#e3f2fd'
                                  : row.abc_classification === 'C' ? '#fff3e0'
                                  : row.abc_classification === 'D' ? '#fce4ec'
                                  : row.abc_classification === 'E' ? '#f5f5f5' : '#f5f5f5',
                                color: row.abc_classification === 'A' ? '#2e7d32'
                                  : row.abc_classification === 'B' ? '#1565c0'
                                  : row.abc_classification === 'C' ? '#e65100'
                                  : row.abc_classification === 'D' ? '#c62828'
                                  : row.abc_classification === 'E' ? '#616161' : '#616161',
                              }}
                            />
                          ) : (
                            cellValue(row, col.key)
                          )}
                        </TableCell>
                      ))}

                      {/* Monthly editable cells */}
                      {monthHeaders.map((header) => {
                        const original = row.monthly_forecast?.[header] || 0;
                        const key = mkKey(row.product_id, row.store_id, header);
                        const entry = localOverrides.get(key);
                        const displayVal = entry ? entry.override : original;
                        const isEditing = editingCell === key;
                        const hasChange = entry && entry.override !== entry.original;
                        const changePct = hasChange && entry!.original > 0
                          ? ((entry!.override - entry!.original) / entry!.original * 100)
                          : 0;

                        rowTotal += displayVal;

                        return (
                          <TableCell
                            key={header} align="right"
                            onClick={() => handleCellClick(key, original)}
                            sx={{
                              fontSize: '11px', fontVariantNumeric: 'tabular-nums',
                              cursor: 'pointer', position: 'relative', px: '4px',
                              bgcolor: hasChange
                                ? (changePct > 0 ? 'rgba(46, 125, 50, 0.08)' : 'rgba(198, 40, 40, 0.08)')
                                : 'inherit',
                              '&:hover': { bgcolor: hasChange ? undefined : 'rgba(0,0,0,0.04)' },
                              minWidth: 48,
                            }}
                          >
                            {isEditing ? (
                              <TextField
                                inputRef={editRef}
                                type="number" size="small"
                                value={entry?.override ?? original}
                                onChange={(e) => handleOverrideValueChange(key, e.target.value)}
                                onBlur={handleCellBlur}
                                onKeyDown={handleCellKeyDown}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                                inputProps={{ style: { textAlign: 'right', width: 46, fontSize: '11px', padding: '3px 4px' } }}
                                sx={{ '& .MuiOutlinedInput-root': { minHeight: 0 } }}
                              />
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: '11px',
                                    fontWeight: hasChange ? 700 : 400,
                                    color: hasChange
                                      ? (changePct > 0 ? '#2e7d32' : '#c62828')
                                      : 'inherit',
                                  }}
                                >
                                  {displayVal.toLocaleString()}
                                </Typography>
                                {hasChange && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '9px', fontWeight: 600, lineHeight: 1,
                                      color: changePct > 0 ? '#2e7d32' : '#c62828',
                                    }}
                                  >
                                    {changePct > 0 ? '+' : ''}{changePct.toFixed(1)}%
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        );
                      })}

                      {/* Total column */}
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                          borderLeft: '2px solid var(--color-border)', px: '4px',
                        }}
                      >
                        {rowTotal.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Empty states */}
      {projectionData && dataRows.length === 0 && !isLoading && (
        <Alert severity="info">No data available for the selected filters.</Alert>
      )}

      {!shouldLoad && !isLoading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Edit sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            Click "Load Grid" to view the forecast grid. Click any cell to override its value.
          </Typography>
        </Paper>
      )}
    </>
  );

  return (
    <Box>
      {/* Fullscreen wrapper */}
      {isFullscreen ? (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 1300,
          bgcolor: 'var(--color-surface)', p: 3, overflow: 'auto',
        }}>
          {tableContent}
        </Box>
      ) : (
        tableContent
      )}

      {/* Reset confirmation dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset All Overrides?</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete all saved overrides matching the current filters and revert to the initial forecast values.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetOverrides} color="error" variant="contained">
            Reset All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManualOverrideTab;
